import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from "@headlessui/react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { DeckFormatEnum, DeckVisibilityEnum } from "@sorcery/db";
import {
  Button,
  Checkbox,
  Combobox,
  Input,
  Line,
  ModalBody,
  ModalHeader,
  P,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@sorcery/ui";

import { Textarea } from "~/components/atoms/Textarea";
import { useModal } from "~/providers/ModalProvider";
import { deckFormatOptions } from "~/utils/filters";
import { deckPath } from "~/utils/paths";
import type { RouterOutputs } from "~/utils/trpc";
import { api } from "~/utils/trpc";

type AvatarSearchType = RouterOutputs["card"]["getAllAvatars"][0];

export const DeckCreateModal = () => {
  const { data: avatars } = api.card.getAllAvatars.useQuery(undefined, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { closeModal } = useModal();
  const { push } = useRouter();
  const trpcUtils = api.useUtils();

  const {
    mutateAsync: createDeck,
    error: trpcError,
    isPending,
  } = api.deck.create.useMutation({
    onSuccess: (id) => {
      toast("Deck created");
      void trpcUtils.deck.getLatest.invalidate();
      void trpcUtils.deck.getMyLatest.invalidate();
      void push(deckPath(id));
      closeModal();
    },
  });

  const [deckName, setDeckName] = useState("");
  const [format, setFormat] = useState<DeckFormatEnum>(DeckFormatEnum.Constructed);
  const [visibility, setVisibility] = useState<DeckVisibilityEnum>(DeckVisibilityEnum.Private);
  const [avatar, setAvatar] = useState<AvatarSearchType>();
  const [bulkAdd, setBulkAdd] = useState("");

  const [avatarMissingError, setAvatarMissingError] = useState(false);
  const [nameMissingError, setNameMissingError] = useState(false);
  const [error, setError] = useState("");

  // Track trpc errors for display
  useEffect(() => {
    if (trpcError) {
      if (trpcError.shape?.data.code === "BAD_REQUEST") {
        setError(trpcError.shape?.message ?? "Something went wrong. Try again.");
      } else {
        // don't expose server side error messages
        setError("Something went wrong.");
      }
    }
  }, [trpcError]);

  const onCreate = () => {
    // clear errs
    setError("");
    setNameMissingError(false);
    setAvatarMissingError(false);

    if (!deckName) return setNameMissingError(true);
    if (!avatar) return setAvatarMissingError(true);

    void createDeck({
      name: deckName,
      format,
      visibility,
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      featureId: avatar.variants[0]!.id,
      bulkAdd,
      avatars: [
        {
          cardId: avatar.id,
          quantity: 1,
          // biome-ignore lint/style/noNonNullAssertion: <explanation>
          variantId: avatar.variants[0]!.id,
        },
      ],
      elements: [],
    });
  };

  return (
    <div className="p-6">
      <ModalHeader title="Create Deck" />

      <ModalBody>
        {/* item */}
        <div className="mb-2 w-full">
          <div className="mb-1">Name</div>
          <div>
            <Input
              onChange={setDeckName}
              error={nameMissingError}
              placeholder="Name your deck..."
            />
          </div>
        </div>

        {/* item */}
        <div className="mb-2 w-full">
          <div className="mb-1">Avatar</div>
          <Combobox
            options={(avatars ?? []).map((a) => ({
              label: a.name,
              value: a.id,
              data: a,
            }))}
            onSelect={(o) => setAvatar(o.data as AvatarSearchType)}
          />

          {avatarMissingError && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {"Please select an avatar."}
                <span>.</span>
              </p>
            </div>
          )}
        </div>

        {/* item */}
        <div className="mb-2 flex">
          <div className="mr-1 w-1/2">
            <div className="mb-1">Format</div>
            <Select onValueChange={(v) => setFormat(v as DeckFormatEnum)}>
              <SelectTrigger arrow>
                <SelectValue
                  placeholder={
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    deckFormatOptions.find(
                      (d) => (d.value as DeckFormatEnum) === DeckFormatEnum.Constructed,
                    )!.label
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {deckFormatOptions.map((d) => (
                  <SelectItem key={d.label} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-4 w-1/2">
            <div className="mb-1">Visibility</div>
            <div className="flex space-x-4 pt-2">
              {Object.values(DeckVisibilityEnum).map((v) => (
                <div
                  className="flex items-center space-x-2"
                  key={v}
                  onClick={() => setVisibility(v)}
                >
                  <Checkbox id={v} checked={visibility === v} label={v} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* description */}
        <div className="max-w-sm">
          <P className="mt-4">
            Private decks won't appear on our homepage, in public deck searches, or on your public
            profile. You can still share private decks to your friends by sending them a link, and
            import private decks to TTS.
          </P>

          <P className="mt-4">Dont worry, this can all be changed later.</P>
        </div>

        {/* bulk add */}
        <Disclosure>
          {({ open }) => (
            <>
              <DisclosureButton className={"flex flex-row items-center justify-between text-left"}>
                <P className="!mb-2 italic underline">
                  <span>{open ? "Close" : "Open"}</span>
                  <span>&nbsp;bulk add</span>
                </P>
              </DisclosureButton>

              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <DisclosurePanel>
                  <P className="!mb-2 italic">
                    Add 1 item per line in the format "quantity name" e.g. "4 Cave Trolls"
                  </P>
                  <Textarea placeholder="Add decklist here..." onChange={setBulkAdd} />
                </DisclosurePanel>
              </Transition>
            </>
          )}
        </Disclosure>

        {/* error */}
        {error && (
          <div className="text-red my-4" role="alert">
            <p>
              <span className="font-bold">Error. </span>
              {error}
              <span>.</span>
            </p>
          </div>
        )}
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button onClick={onCreate} disabled={isPending}>
          Create
        </Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
